import { initializeApp } from "firebase/app";
import { getVertexAI, getGenerativeModel } from "firebase/vertexai";
import { getMessaging } from "firebase/messaging";
import { getFunctions } from "firebase/functions";

import {
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from "firebase/auth";
import { ref, deleteObject, getStorage } from "firebase/storage";

import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  writeBatch,
  deleteDoc,
  query,
  where,
  addDoc,
  orderBy,
  onSnapshot,
  CACHE_SIZE_UNLIMITED,
  initializeFirestore,
  enableMultiTabIndexedDbPersistence,
  updateDoc,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const messaging = getMessaging(app);

// Initialize Functions with region
export const functions = getFunctions(app, "us-central1");

// Initialize Firestore with settings
export const db = initializeFirestore(app, {
  cacheSizeBytes: CACHE_SIZE_UNLIMITED,
  experimentalForceLongPolling: true,
  useFetchStreams: false,
});

// Enable offline persistence with multi-tab support
const enablePersistence = async () => {
  try {
    await enableMultiTabIndexedDbPersistence(db);
  } catch (err) {
    if (err.code === "failed-precondition") {
      // Multiple tabs open, persistence can only be enabled in one tab at a time.
      console.warn(
        "Multiple tabs open, persistence can only be enabled in one tab at a time."
      );
    } else if (err.code === "unimplemented") {
      // The current browser doesn't support persistence
      console.warn("The current browser does not support persistence.");
    }
  }
};

// Initialize persistence
enablePersistence();

// Initialize the Vertex AI service
const vertexAI = getVertexAI(app);

// Initialize the generative model with a model that supports your use case
export const geminiModel = getGenerativeModel(vertexAI, {
  model: "gemini-2.0-flash",
});

const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: "select_account" });

export const auth = getAuth();
export const storage = getStorage(app);

export default app;

export const signInWithEmail = async (email, password) => {
  try {
    const response = await signInWithEmailAndPassword(auth, email, password);
    return response;
  } catch (error) {
    let errorMessage = "An error occurred during sign in";
    let errorCode = 400;

    switch (error.code) {
      case "auth/user-not-found":
        errorMessage = "User not found, please sign up instead";
        break;
      case "auth/wrong-password":
        errorMessage = "INVALID_LOGIN_CREDENTIALS";
        break;
      case "auth/too-many-requests":
        errorMessage = "TOO_MANY_ATTEMPTS_TRY_LATER";
        break;
      case "auth/invalid-email":
        errorMessage = "Invalid email format";
        break;
      default:
        errorMessage = error.message;
    }

    const customError = {
      code: errorCode,
      message: errorMessage,
    };
    throw customError;
  }
};

export const findUserDocbyId = async (id) => {
  const userRef = doc(db, "users", id);
  const snapShot = await getDoc(userRef);
  if (snapShot.exists()) {
    return snapShot.data();
  } else {
    return false;
  }
};

export const LogOutUser = async () => {
  try {
    await signOut(auth);
    return true;
  } catch (error) {
    console.error("Error signing out:", error);
    return false;
  }
};

export const createNewDocument = async (collectionName, data) => {
  try {
    const collectionRef = collection(db, collectionName);
    const docRef = await addDoc(collectionRef, data);
    return docRef;
  } catch (error) {
    alert(error.message);
    return error;
  }
};

export const addNewDoc = async (collectionName, data) => {
  try {
    const collectionRef = collection(db, collectionName);
    const docRef = await addDoc(collectionRef, data);
    return docRef;
  } catch (error) {
    alert(error.message);
    return error;
  }
};

export const getAllDocs = async (collectionName) => {
  try {
    const collectionRef = collection(db, collectionName);
    const querySnapshot = await getDocs(collectionRef);
    const docs = [];
    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs;
  } catch (error) {
    return error;
  }
};

export const getAllPhotosById = async (collectionName, id) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where("listingId", "==", id));
    const querySnapshot = await getDocs(q);
    const docs = [];
    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs;
  } catch (error) {
    return error;
  }
};

export const getAllBuildingPhotosById = async (collectionName, id) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where("buildingId", "==", id));
    const querySnapshot = await getDocs(q);
    const docs = [];
    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs;
  } catch (error) {
    return error;
  }
};

export const getAllDocsSnap = (collectionName, callback) => {
  try {
    const collectionRef = collection(db, collectionName);

    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(collectionRef, (querySnapshot) => {
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ ...doc.data(), id: doc.id });
      });
      // Invoke the callback with the updated data
      callback(docs);
    });

    // Return the unsubscribe function to stop listening to updates when needed
    return unsubscribe;
  } catch (error) {
    console.error("Error fetching documents:", error);
  }
};

export const getAllBuildingSpaces = (collectionName, id, callback) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where("buildingId", "==", id));

    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ ...doc.data(), id: doc.id });
      });
      // Invoke the callback with the updated data
      callback(docs);
    });

    // Return the unsubscribe function to stop listening to updates when needed
    return unsubscribe;
  } catch (error) {
    console.error("Error fetching documents:", error);
  }
};

export const getAllBuildingPhotosSnap = (collectionName, id, callback) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where("buildingId", "==", id));

    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ ...doc.data(), id: doc.id });
      });
      // Invoke the callback with the updated data
      callback(docs);
    });

    // Return the unsubscribe function to stop listening to updates when needed
    return unsubscribe;
  } catch (error) {
    console.error("Error fetching documents:", error);
  }
};

export const getAllTasks = (collectionName, id, callback) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where("listing_id", "==", id));

    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ ...doc.data(), id: doc.id });
      });
      // Invoke the callback with the updated data
      callback(docs);
    });

    // Return the unsubscribe function to stop listening to updates when needed
    return unsubscribe;
  } catch (error) {
    console.error("Error fetching documents:", error);
  }
};

export const getAllUserTasks = (collectionName, email, callback) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(
      collectionRef,
      where("assignee_emails", "array-contains", email),
      where("status", "==", "Open")
    );

    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ ...doc.data(), id: doc.id });
      });
      // Invoke the callback with the updated data
      callback(docs);
    });

    // Return the unsubscribe function to stop listening to updates when needed
    return unsubscribe;
  } catch (error) {
    console.error("Error fetching documents:", error);
  }
};

export const setDocData = async (collectionName, id, data) => {
  try {
    const collectionRef = collection(db, collectionName);
    const docRef = doc(collectionRef, id);
    await setDoc(docRef, data, { merge: true });
    return true;
  } catch (error) {
    alert(error);
    return error;
  }
};

export const getAllDocsByEmail = async (collectionName, email, field) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where(field, "==", email));
    const querySnapshot = await getDocs(q);
    const docs = [];
    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs;
  } catch (error) {
    return error;
  }
};

export const getAllAccounts = async (collectionName, email, field) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where(field, "array-contains", email));
    const querySnapshot = await getDocs(q);
    const docs = [];
    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs;
  } catch (error) {
    return error;
  }
};

export const getDocById = async (collectionName, id) => {
  try {
    const collectionRef = collection(db, collectionName);
    const docRef = doc(collectionRef, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return false;
    }
  } catch (error) {
    return error;
  }
};

export const getDocData = async (collectionName, id) => {
  try {
    const collectionRef = collection(db, collectionName);
    const docRef = doc(collectionRef, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return false;
    }
  } catch (error) {
    return error;
  }
};

export const getUserByEmail = async (collectionName, email) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where("email", "==", email));
    const querySnapshot = await getDocs(q);
    const docs = [];

    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs[0];
  } catch (error) {
    return error;
  }
};

export const getUserByEmailSnap = (collectionName, email, callback) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where("email", "==", email));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ ...doc.data(), id: doc.id });
      });
      callback(docs[0]);
    });
    return unsubscribe;
  } catch (error) {
    return error;
  }
};

export const getDocDataSnap = (collectionName, id, callback) => {
  try {
    const collectionRef = collection(db, collectionName);
    const docRef = doc(collectionRef, id);

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        callback(docSnap.data());
      } else {
        callback(false);
      }
    });

    // Return the unsubscribe function so that you can stop listening when needed
    return unsubscribe;
  } catch (error) {
    console.error("Error fetching document:", error);
    // Return a no-op function instead of throwing to prevent unsubscribe errors
    return () => {};
  }
};

export const updateDocById = async (collectionName, id, data) => {
  try {
    const collectionRef = collection(db, collectionName);
    const docRef = doc(collectionRef, id);
    await setDoc(docRef, data, { merge: true });
    return true;
  } catch (error) {
    return error;
  }
};

export const deleteDocById = async (collectionName, id) => {
  try {
    const collectionRef = collection(db, collectionName);
    const docRef = doc(collectionRef, id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    return error;
  }
};

export const countDocsByStatusAndEmail = async (
  collectionName,
  email,
  status,
  field
) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(
      collectionRef,
      where("status", "==", status),
      where(field, "==", email)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    return error;
  }
};

export const getAllDocsByEmailAndStatus = async (
  collectionName,
  email,
  status,
  field
) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(
      collectionRef,
      where("status", "==", status),
      where(field, "==", email)
    );
    const querySnapshot = await getDocs(q);
    const docs = [];
    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs;
  } catch (error) {
    return error;
  }
};

export const signupWithEmail = async (email, password) => {
  try {
    const response = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    return response;
  } catch (error) {
    if (error.code === "auth/email-already-in-use") {
      alert("Email already in use, please sign in instead");
    }
  }
};

export const updateUserDisplayName = async (displayName) => {
  try {
    const user = auth.currentUser;
    await user.updateProfile({
      displayName: displayName,
    });
    return true;
  } catch (error) {
    return error;
  }
};

export const checkUser = async (userAuth) => {
  if (userAuth) {
    //check if user is existing
    const userRef = doc(db, "users", userAuth.uid);
    const userSnapShot = await getDoc(userRef);

    if (!userSnapShot.exists()) {
      return false;
    } else {
      return true;
    }
  }
};

export const createUserDoc = async (userAuth, additionalData) => {
  if (!userAuth) return;

  const colref = collection(db, "users");
  const userRef = doc(colref, userAuth.uid);
  const snapShot = await getDoc(userRef);

  if (!snapShot.exists()) {
    const { displayName, email } = userAuth;
    const createdAt = new Date();
    try {
      await setDoc(userRef, {
        displayName,
        email,
        createdAt,
        ...additionalData,
      });
    } catch (error) {
      alert(error.message);
    }
  }
  return userRef;
};

export const queryAllDocs = async (collectionName, field, order) => {
  try {
    const q = query(collection(db, collectionName), orderBy(field, order));
    const querySnapshot = await getDocs(q);
    const docs = [];
    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs;
  } catch (error) {
    return error;
  }
};

export const queryAllDocsByField = async (
  collectionName,
  field,
  whereClause,
  value
) => {
  try {
    const q = query(
      collection(db, collectionName),
      where(field, whereClause, value)
    );
    const querySnapshot = await getDocs(q);
    const docs = [];
    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs;
  } catch (error) {
    return error;
  }
};

export const queryAllDocsByFieldSnap = (
  collectionName,
  field,
  whereClause,
  value,
  callback
) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where(field, whereClause, value));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ ...doc.data(), id: doc.id });
      });
      callback(docs);
    });
    return unsubscribe;
  } catch (error) {
    return error;
  }
};

export const queryOnSnapshot = async (
  collectionName,
  field,
  whereClause,
  value,
  callback
) => {
  try {
    const q = query(
      collection(db, collectionName),
      where(field, whereClause, value),
      orderBy("created_at", "desc")
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ ...doc.data(), id: doc.id });
      });
      callback(docs);
    });
    return unsubscribe;
  } catch (error) {
    return error;
  }
};

export const queryAllListings = async (
  collectionName,
  field,
  whereClause,
  value
) => {
  try {
    const q = query(
      collection(db, collectionName),
      where(field, whereClause, value),
      orderBy("created_at", "desc")
    );
    const querySnapshot = await getDocs(q);
    const docs = [];
    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs;
  } catch (error) {
    return error;
  }
};

export const resetpassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    return error;
  }
};

export const deleteObjectByUrl = async (url) => {
  try {
    const storageRef = ref(storage, url);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    return error;
  }
};

export const uploadBatchDocs = async (collectionName, data) => {
  try {
    const batch = writeBatch(db);
    data.forEach((item) => {
      const docRef = doc(collection(db, collectionName));
      batch.set(docRef, item);
    });
    await batch.commit();
    alert("Batch upload successful");
    return true;
  } catch (error) {
    console.error(error.message);
    return error;
  }
};

export const getAllDocsSnapByEmail = (collectionName, email, callback) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(
      collectionRef,
      where("team_members", "array-contains", email)
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ ...doc.data(), id: doc.id });
      });
      callback(docs);
    });
    return unsubscribe;
  } catch (error) {
    return error;
  }
};

export const queryIdentifiers = async (collectionName, field, value) => {
  try {
    const q = query(collection(db, collectionName), where(field, "==", value));
    const querySnapshot = await getDocs(q);
    const docs = [];
    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs;
  } catch (error) {
    return error;
  }
};

export const queryDocByFieldSnap = (
  collectionName,
  field,
  whereClause,
  value,
  callback
) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where(field, whereClause, value));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ ...doc.data(), id: doc.id });
      });
      callback(docs);
    });
    return unsubscribe;
  } catch (error) {
    return error;
  }
};

export const queryDocByField = async (
  collectionName,
  field,
  whereClause,
  value
) => {
  try {
    const q = query(
      collection(db, collectionName),
      where(field, whereClause, value)
    );
    const querySnapshot = await getDocs(q);
    const docs = [];
    querySnapshot.forEach((doc) => {
      docs.push({ ...doc.data(), id: doc.id });
    });
    return docs;
  } catch (error) {
    return error;
  }
};

// Chat-related utility functions
export const createDirectChat = async (
  user1Id,
  user2Id,
  productContext = null
) => {
  if (!productContext) {
    // For regular direct chats without product context
    const chatId = [user1Id, user2Id].sort().join("_");
    const chatRef = doc(db, "directChats", chatId);
    const chatSnap = await getDoc(chatRef);

    if (!chatSnap.exists()) {
      await setDoc(chatRef, {
        id: chatId,
        members: [user1Id, user2Id],
        messages: [],
        createdAt: new Date(),
        lastMessage: null,
        lastMessageTime: null,
        type: "direct",
        unreadCount: {
          [user1Id]: 0,
          [user2Id]: 0,
        },
      });
    }
    return chatId;
  }

  // For marketplace/property chats
  const contextId = productContext.productId || productContext.propertyId;
  const chatId = `${[user1Id, user2Id].sort().join("_")}_${contextId}`;
  const chatRef = doc(db, "directChats", chatId);
  const chatSnap = await getDoc(chatRef);

  if (!chatSnap.exists()) {
    await setDoc(chatRef, {
      id: chatId,
      members: [user1Id, user2Id],
      messages: [],
      createdAt: new Date(),
      lastMessage: null,
      lastMessageTime: null,
      productContext: productContext,
      type: "direct",
      unreadCount: {
        [user1Id]: 0,
        [user2Id]: 0,
      },
    });
  }
  return chatId;
};

export const getDirectChats = async (userId) => {
  const q = query(
    collection(db, "directChats"),
    where("members", "array-contains", userId),
    orderBy("lastMessageTime", "desc")
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const getDirectChatsSnap = (userId, callback) => {
  const q = query(
    collection(db, "directChats"),
    where("members", "array-contains", userId),
    orderBy("lastMessageTime", "desc")
  );

  return onSnapshot(q, (querySnapshot) => {
    const chats = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(chats);
  });
};

export const markChatAsRead = async (chatId, userId) => {
  const chatRef = doc(db, "directChats", chatId);
  await updateDoc(chatRef, {
    [`unreadCount.${userId}`]: 0,
  });
};

export const addMessageToChat = async (chatId, isGroupChat, message) => {
  const collectionName = isGroupChat ? "groupChats" : "directChats";
  const chatRef = doc(db, collectionName, chatId);
  const chatSnap = await getDoc(chatRef);

  if (chatSnap.exists()) {
    const messages = chatSnap.data().messages || [];
    await updateDoc(chatRef, {
      messages: [...messages, message],
      lastMessage: message.message,
      lastMessageTime: message.dateTime,
    });
  }
};

export const getAllPendingRequests = async (userEmail) => {
  try {
    const collections = ["workPermits", "gatePass", "concerns"];
    const allPendingRequests = [];

    for (const collectionName of collections) {
      const collectionRef = collection(db, collectionName);
      const q = query(
        collectionRef,
        where("user", "==", userEmail),
        where("status", "in", [
          "pending",
          "Pending",
          "Returned",
          "Pending payment",
        ])
      );

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        allPendingRequests.push({
          ...doc.data(),
          id: doc.id,
          collectionType: collectionName,
        });
      });
    }

    return allPendingRequests;
  } catch (error) {
    console.error("Error fetching pending requests:", error);
    return [];
  }
};

// Set up auth state listener that updates Redux store
export const setupAuthListener = (dispatch, loadUserAction) => {
  return onAuthStateChanged(auth, async (userAuth) => {
    if (userAuth) {
      // User is signed in
      // Get user data from Firestore
      const userData = await findUserDocbyId(userAuth.uid);
      if (userData) {
        // Dispatch user data to Redux
        dispatch(
          loadUserAction({
            uid: userAuth.uid,
            email: userAuth.email,
            displayName: userAuth.displayName,
            ...userData,
          })
        );
      } else {
        // Just dispatch auth data if no additional user data exists
        dispatch(
          loadUserAction({
            uid: userAuth.uid,
            email: userAuth.email,
            displayName: userAuth.displayName,
          })
        );
      }
    } else {
      // User is signed out
      dispatch(loadUserAction([]));
    }
  });
};

// Category Management Functions
export const getAllCategories = async () => {
  try {
    const collectionRef = collection(db, "categories");
    const querySnapshot = await getDocs(collectionRef);
    const categories = [];
    querySnapshot.forEach((doc) => {
      categories.push({ ...doc.data(), id: doc.id });
    });
    return categories;
  } catch (error) {
    console.error("Error fetching categories:", error);
    return [];
  }
};

export const addNewCategory = async (categoryData) => {
  try {
    const collectionRef = collection(db, "categories");
    const docRef = await addDoc(collectionRef, {
      ...categoryData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return { ...categoryData, id: docRef.id };
  } catch (error) {
    console.error("Error adding category:", error);
    throw error;
  }
};

export const getAllCategoriesSnap = (callback) => {
  try {
    const collectionRef = collection(db, "categories");
    const unsubscribe = onSnapshot(collectionRef, (querySnapshot) => {
      const categories = [];
      querySnapshot.forEach((doc) => {
        categories.push({ ...doc.data(), id: doc.id });
      });
      callback(categories);
    });
    return unsubscribe;
  } catch (error) {
    console.error("Error setting up categories listener:", error);
    return () => {};
  }
};

// Like Management Functions
export const toggleLike = async (itemId, userId, itemType) => {
  try {
    const likesRef = doc(db, `${itemType}Likes`, itemId);
    const likeDoc = await getDoc(likesRef);

    if (likeDoc.exists()) {
      const likes = likeDoc.data().likes || [];
      const userIndex = likes.indexOf(userId);

      if (userIndex === -1) {
        // Add like
        await updateDoc(likesRef, {
          likes: [...likes, userId],
          count: likes.length + 1,
        });
        return true;
      } else {
        // Remove like
        likes.splice(userIndex, 1);
        await updateDoc(likesRef, {
          likes: likes,
          count: likes.length,
        });
        return false;
      }
    } else {
      // Create new likes document
      await setDoc(likesRef, {
        likes: [userId],
        count: 1,
      });
      return true;
    }
  } catch (error) {
    console.error("Error toggling like:", error);
    throw error;
  }
};

export const getLikes = async (itemId, itemType) => {
  try {
    const likesRef = doc(db, `${itemType}Likes`, itemId);
    const likeDoc = await getDoc(likesRef);

    if (likeDoc.exists()) {
      return likeDoc.data();
    }
    return { likes: [], count: 0 };
  } catch (error) {
    console.error("Error getting likes:", error);
    return { likes: [], count: 0 };
  }
};

export const getLikesSnap = (itemId, itemType, callback) => {
  try {
    const likesRef = doc(db, `${itemType}Likes`, itemId);
    return onSnapshot(likesRef, (doc) => {
      if (doc.exists()) {
        callback(doc.data());
      } else {
        callback({ likes: [], count: 0 });
      }
    });
  } catch (error) {
    console.error("Error setting up likes listener:", error);
    callback({ likes: [], count: 0 });
    return () => {};
  }
};
